import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { academyService } from '../../services/academy';
import { useAcademySlug } from '../../hooks/useAcademySlug';
import type { Academy } from '@court-sync/shared';
import { AcademyBrandingProvider } from '@/contexts/AcademyBrandingContext';
import { ResetPasswordForm } from '../../components/auth/ResetPasswordForm';
import {
  buildAcademyLoginPath,
  buildAcademyForgotPasswordPath,
  getAcademyHostMode,
} from '../../lib/academyPaths';
import { isStarterMemberAuthBlockedAcademy } from '../../utils/publicBrowseMode';
import { StarterMemberAuthNotFound } from '../../components/public/StarterMemberAuthNotFound';

export default function AcademyResetPasswordPage() {
  const academySlug = useAcademySlug();
  const [searchParams] = useSearchParams();
  const [academy, setAcademy] = useState<Academy | null>(null);
  const [loadingAcademy, setLoadingAcademy] = useState(() => Boolean(academySlug));
  const [academyError, setAcademyError] = useState<string | null>(null);

  const returnUrl = searchParams.get('returnUrl') ?? undefined;
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  useEffect(() => {
    if (!academySlug) return;
    academyService
      .getAcademyBySlug(academySlug)
      .then((a) => {
        setAcademy(a);
        setAcademyError(null);
      })
      .catch(() => {
        setAcademy(null);
        setAcademyError('Academy not found');
      })
      .finally(() => setLoadingAcademy(false));
  }, [academySlug]);

  const loginPath = academy
    ? buildAcademyLoginPath(academy, returnUrl, getAcademyHostMode())
    : '/login';
  const forgotPasswordPath = academy
    ? buildAcademyForgotPasswordPath(academy, returnUrl, getAcademyHostMode())
    : '/forgot-password';

  const hasValidCode = Boolean(oobCode && mode === 'resetPassword');

  if (!academySlug) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <p className="text-red-600 dark:text-red-400 mb-4">Academy not found</p>
        <Link to="/login" className="text-primary-600 hover:text-primary-500 dark:text-primary-400">
          Go to main login
        </Link>
      </div>
    );
  }

  if (loadingAcademy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  if (academyError || !academy) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <p className="text-red-600 dark:text-red-400 mb-4">{academyError}</p>
        <Link to="/login" className="text-primary-600 hover:text-primary-500 dark:text-primary-400">
          Go to main login
        </Link>
      </div>
    );
  }

  if (isStarterMemberAuthBlockedAcademy(academy)) {
    return <StarterMemberAuthNotFound academy={academy} academySlug={academySlug} />;
  }

  return (
    <AcademyBrandingProvider primaryColor={academy.primaryColor} secondaryColor={academy.secondaryColor}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-tennis-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              {academy.logoUrl ? (
                <img
                  src={academy.logoUrl}
                  alt={academy.name}
                  className="h-16 w-auto mx-auto mb-4 object-contain"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="flex justify-center mb-6">
                  <img src="/icon.png" alt="CourtSync" className="h-[60px] w-[60px]" width={60} height={60} />
                </div>
              )}
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{academy.name}</h1>
              <p className="text-gray-600 dark:text-gray-400">Reset your password</p>
            </div>

            {hasValidCode ? (
              <ResetPasswordForm oobCode={oobCode!} loginPath={loginPath} />
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center dark:border-amber-800 dark:bg-amber-900/20">
                <AlertCircle className="h-10 w-10 text-amber-600 dark:text-amber-400 mx-auto mb-3" aria-hidden />
                <p className="text-sm text-amber-900 dark:text-amber-200 mb-4">
                  This reset link is missing or invalid. Please request a new password reset email.
                </p>
                <Link
                  to={forgotPasswordPath}
                  className="text-sm font-medium text-primary-600 hover:text-primary-800 dark:text-primary-300"
                >
                  Request a new reset link
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </AcademyBrandingProvider>
  );
}
